import { useMemo } from 'react';

import { startOperation } from '@mint-lib/datadog';
import { TFunction, useTranslation } from '@mint-lib/i18n';
import { Add } from '@mint-lib/icons';
import { usePermissions } from '@mint-lib/profile-helpers';
import { useFlags } from '@mint-lib/routing-context';
import { Button } from '@mint-lib/tw-components';

import { PERMISSIONS_MAP } from '../../constants.js';
import { useModal } from '../../context/ModalContext/useModal.js';
import useTab, { RouteTabs } from '../../hooks/useTab/useTab.js';

export const CreateModalLabelMap: (
  t: TFunction,
) => Record<RouteTabs, string> = (t: TFunction) => ({
  [RouteTabs.User]: t('New user'),
  [RouteTabs.Role]: t('New role'),
});

const AddButton = () => {
  const { t } = useTranslation('@myn/permissions');
  const ff = useFlags('@mint/arm', [
    'mint2Permissions',
    'usersManagementAgGrid',
  ]);

  const { setOpen: setIsNewModalOpened } = useModal();

  const activeRoute = useTab();

  const { getPermissionField } = usePermissions();

  const isReadonlyPermission = useMemo(
    () =>
      getPermissionField('usersPermissionsFeature') === PERMISSIONS_MAP.View,
    [getPermissionField('usersPermissionsFeature')],
  );

  const label = CreateModalLabelMap(t)[activeRoute ?? RouteTabs.User];

  if (isReadonlyPermission) {
    return null;
  }

  // One button per tab, so each reports its own action name.
  return activeRoute === RouteTabs.Role ? (
    <Button
      variant="contained"
      color="primary"
      size="large"
      onClick={() => {
        // Ended by CreateRoleModal on the role page.
        startOperation('role.create', {
          context: { mint2Permissions: ff.mint2Permissions.enabled },
        });
        setIsNewModalOpened(true);
      }}
      aria-label="open add modal"
      data-dd-action-name="User management: new role"
    >
      <Add />
      {label}
    </Button>
  ) : (
    <Button
      variant="contained"
      color="primary"
      size="large"
      onClick={() => {
        // Ended by NewUserModal on the user page.
        startOperation('user.invite', {
          context: {
            usersManagementAgGrid: ff.usersManagementAgGrid.enabled,
          },
        });
        setIsNewModalOpened(true);
      }}
      aria-label="open add modal"
      data-dd-action-name="User management: new user"
    >
      <Add />
      {label}
    </Button>
  );
};

export default AddButton;
